import {Component, forwardRef, inject, Input, OnInit} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {AvailableCalculationOperators} from '@em/shared/mapping-engine/data-access';
import {
  BackendCalculationOperator,
  CalculationMappingValue,
  Operand,
} from '@em/shared/mapping-engine/domain-models';
import {
  EmIconModule,
  EmInputModule,
  EmMoreInfoModule,
  EmSelectModule,
} from '@em/shared/ui';
import {TranslateModule} from '@ngx-translate/core';
import {tap} from 'rxjs/operators';
import {EmSelectOperandModule} from '../select-operand/select-opearnd.module';

@Component({
  selector: 'em-calculation-mapping',
  templateUrl: './calculation-mapping.component.html',
  styleUrls: ['./calculation-mapping.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmCalculationMappingComponent),
      multi: true,
    },
  ],
  imports: [
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    EmSelectOperandModule,
    EmSelectModule,
    EmInputModule,
    EmIconModule,
    EmMoreInfoModule,
  ],
})
export class EmCalculationMappingComponent
  implements ControlValueAccessor, OnInit
{
  private readonly _fb = inject(FormBuilder);

  private _onChangeCallback?: (val: CalculationMappingValue) => void;

  @Input() availableAttributes: string[] | undefined | null;
  @Input() customAttributes: string[] | undefined | null;

  readonly formGroup = this._fb.group({
    attribute: new FormControl<Operand | null>(null, {
      validators: [Validators.required],
    }),
    operator: new FormControl<BackendCalculationOperator | null>(null, {
      validators: [Validators.required],
    }),
    value: new FormControl<number | null>(null, {
      validators: [Validators.required],
    }),
  });
  readonly AvailableOperators = AvailableCalculationOperators;

  get operatorValue() {
    return this.formGroup.value.operator;
  }

  ngOnInit(): void {
    this.formGroup.valueChanges
      .pipe(tap(() => this._emitChanges()))
      .subscribe();
  }

  //#region ControlValueAccessor implementation
  writeValue(mappingValue: CalculationMappingValue | null): void {
    this.formGroup.setValue(
      {
        attribute: mappingValue?.attribute_name
          ? {type: 'attribute', value: mappingValue?.attribute_name}
          : null,
        operator: mappingValue?.operator ?? null,
        value: mappingValue?.value ?? null,
      },
      {emitEvent: false},
    );
  }

  registerOnChange(fn: (val: CalculationMappingValue) => void): void {
    this._onChangeCallback = fn;
  }

  registerOnTouched(): void {}
  //#endregion

  private _emitChanges() {
    if (this._onChangeCallback && this.formGroup.valid) {
      this._onChangeCallback({
        attribute_name: this.formGroup.value?.attribute?.value as string,
        operator: this.formGroup.value.operator as BackendCalculationOperator,
        value: this.formGroup.value.value as number,
      });
    }
  }
}
